import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { formatApiErrorDetail } from '../lib/utils';
import { startGoogleLogin } from '../lib/googleAuth';
import { ArrowLeft, Mail, Lock, LogIn, Eye, EyeOff } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, setUser } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 2FA challenge state
  const [mfa, setMfa] = useState(null); // {mfa_token, email}
  const [mfaCode, setMfaCode] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    try {
      const res = await login(formData.email.trim(), formData.password, rememberMe);
      if (res && res.mfa_required) {
        setMfa({ mfa_token: res.mfa_token, email: res.email });
        return;
      }
      toast.success(`Welcome back, ${res.name}!`);

      // Never auto-send anyone to a payment/checkout page on login. Clear any stale
      // pre-login plan selection so it can't trigger an unwanted upgrade redirect.
      try { localStorage.removeItem('studio_subscribe_tier'); } catch (_e) { /* noop */ }

      // Redirect to admin if admin, client portal if client, otherwise home
      if (res.role === 'admin' || res.role === 'super_admin') {
        navigate('/choose-workspace', { replace: true });
      } else if (res.role === 'client') {
        navigate('/portal', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (error) {
      setError(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    if (mfaCode.trim().length < 6) {
      setError('Enter the 6-digit code from your authenticator');
      return;
    }
    setMfaBusy(true);
    setError('');
    try {
      const { default: axios } = await import('axios');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.post(`${API_URL}/api/auth/2fa/verify`, { mfa_token: mfa.mfa_token, code: mfaCode.trim() }, { withCredentials: true });
      const u = res.data;
      setUser(u);
      toast.success(`Welcome back, ${u.name}!`);
      try { localStorage.removeItem('studio_subscribe_tier'); } catch (_e) { /* noop */ }
      if (u.role === 'admin' || u.role === 'super_admin') navigate('/choose-workspace', { replace: true });
      else if (u.role === 'client') navigate('/portal', { replace: true });
      else navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid 2FA code');
    } finally {
      setMfaBusy(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    startGoogleLogin('main', '/');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="login-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] py-4">
        <div className="container-custom">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="DCA Enterprises" className="h-10 w-auto" />
            </Link>
            <Link 
              to="/" 
              className="text-sm text-[#475569] hover:text-[#1D4ED8] flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Back to Website
            </Link>
          </div>
        </div>
      </header>

      {/* Login Form */}
      <div className="flex-grow flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white p-8 border border-[#E2E8F0] shadow-sm">
            <div className="text-center mb-8">
              <h1 className="heading-2 text-2xl text-[#0A0F1C] mb-2" data-testid="login-title">
                Welcome Back
              </h1>
              <p className="text-[#475569] text-sm">
                Sign in to access your account
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            {mfa ? (
              <form onSubmit={submitMfa} className="space-y-4" data-testid="login-mfa-form">
                <div className="bg-[#EFF6FF] border border-[#1D4ED8]/30 rounded-lg p-3 text-xs text-[#1D4ED8]">
                  Two-factor required for <strong>{mfa.email}</strong>. Enter the 6-digit code from your authenticator app, or a recovery code.
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1C] mb-2">6-digit code</label>
                  <Input
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\s+/g, '').slice(0, 12))}
                    placeholder="123 456"
                    className="form-input text-center tracking-widest font-mono text-lg"
                    autoFocus
                    data-testid="login-mfa-code"
                  />
                </div>
                <Button type="submit" disabled={mfaBusy} className="btn-primary-blue w-full" data-testid="login-mfa-submit">
                  {mfaBusy ? 'Verifying…' : 'Verify and sign in'}
                </Button>
                <button type="button" onClick={() => { setMfa(null); setMfaCode(''); setError(''); }} className="w-full text-xs text-[#94A3B8] hover:text-[#1D4ED8]" data-testid="login-mfa-cancel">
                  ← Use a different account
                </button>
              </form>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-[#0A0F1C] mb-2">Email <span className="text-red-500" aria-hidden="true">*</span></label>
                <div className="relative">
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="form-input pl-10"
                    data-testid="login-email-input"
                    required
                    autoComplete="email"
                    aria-required="true"
                  />
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                </div>
              </div>
              
              <div>
                <label htmlFor="login-password" className="block text-sm font-medium text-[#0A0F1C] mb-2">Password <span className="text-red-500" aria-hidden="true">*</span></label>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="form-input pl-10 pr-10"
                    data-testid="login-password-input"
                    required
                    autoComplete="current-password"
                    aria-required="true"
                  />
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#1D4ED8]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="login-password-toggle"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-[#E2E8F0]"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    data-testid="login-remember-me"
                  />
                  <span className="text-[#475569]">Remember me</span>
                </label>
                <Link to="/forgot-password" className="text-[#1D4ED8] hover:underline" data-testid="login-forgot-link">
                  Forgot password?
                </Link>
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="btn-primary-blue w-full flex items-center justify-center gap-2"
                data-testid="login-submit-button"
              >
                {loading ? 'Signing in...' : (
                  <>
                    <LogIn size={18} />
                    Sign In
                  </>
                )}
              </Button>
            </form>
            )}

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#E2E8F0]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-[#94A3B8]">Or continue with</span>
              </div>
            </div>

            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-[#E2E8F0] bg-white hover:bg-[#F9FAFB] transition-colors text-[#0A0F1C] font-medium"
              data-testid="login-google-button"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </button>

            <p className="text-center text-sm text-[#475569] mt-6">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#1D4ED8] font-medium hover:underline" data-testid="login-register-link">
                Create one
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
